import React from "react";
import { Box, Typography, Container, Toolbar, Link as MuiLink } from "@mui/material";
import HomeHeader from "../../Components/HomeHeader";
import HomeFooter from "../../Components/HomeFooter";
import { globalStyles } from "../../Utils/Styles";
import { StyledDivider as Divider } from "../../Components/LightDivider";

/**
 * Quick toggles / single-source-of-truth for company info.
 * Update here and the rest of the document stays in sync.
 */
const COMPANY_NAME = "Blitz Systems Corp.";
const COMPANY_SHORT = "Blitz";
const CONTACT_EMAIL = "legal@useblitz.co";
const CONTACT_ADDRESS = "8724 Cavell Lane, Houston, Texas 77055";
const SITE_NAME = "BlitzPay & Blitz Platform";

const PrivacyPolicy = () => {
  return (
    <Box sx={{ backgroundColor: "#f7f7f8", width: "100%", color: "#000" }}>
      <HomeHeader />
      <Toolbar />

      <Container
        sx={{
          ...globalStyles.wholeHeightMinusToolbar,
          paddingTop: 6,
          paddingBottom: 4,
        }}
      >
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" sx={{ fontWeight: "bold", mb: 1 }}>
            Privacy Policy
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 4 }}>
            Last updated: February 22, 2026
          </Typography>

          {/* Intro */}
          <Typography variant="body1" paragraph>
            {COMPANY_SHORT} ({COMPANY_NAME}) is committed to protecting your privacy. This
            Privacy Policy explains how we collect, use, disclose, and safeguard information
            when you use {SITE_NAME}, including BlitzPay (receivables purchase & payments),
            creator/agency tools, and related services (the "Services").
          </Typography>

          <Divider />

          {/* What we collect */}
          <Box sx={{ my: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: "bold", mb: 2 }}>
              Information We Collect
            </Typography>
            <Typography variant="body1" paragraph>
              We collect information you provide directly and data collected automatically:
            </Typography>
            <Typography variant="body1" component="ul" sx={{ pl: 2 }}>
              <li><strong>Account & Profile:</strong> name, email, phone, social profiles, role, company.</li>
              <li><strong>Business & Compliance:</strong> KYC/KYB data (government IDs, EIN/TIN, address), ownership details, tax forms (e.g., W-9/W-8), sanctions screening results, risk flags.</li>
              <li><strong>Financial & Payments:</strong> bank account details, payout preferences, invoices, statements, receivables and payables metadata, payment history, approvals, and related artifacts (e.g., NOA, RPA).</li>
              <li><strong>Deal & Communications:</strong> brand/label/agency contacts, proposals, contracts, deliverables, message metadata, and opt-in connected email insights (see "Google Workspace APIs").</li>
              <li><strong>Usage & Device:</strong> log data, IP, timestamps, browser/device info, cookies, and similar technologies.</li>
            </Typography>
          </Box>

          <Divider />

          {/* How we use */}
          <Box sx={{ my: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: "bold", mb: 2 }}>
              How We Use Your Information
            </Typography>
            <Typography variant="body1" component="ul" sx={{ pl: 2 }}>
              <li>Provide, maintain, and improve the Services, including onboarding and support.</li>
              <li>Operate BlitzPay: underwrite, purchase, service, and collect on eligible receivables (true-sale where applicable).</li>
              <li>Process payments, payouts, and reconcile accounts.</li>
              <li>Verify identity and business information; perform compliance checks (KYC/KYB, AML, sanctions).</li>
              <li>Detect, investigate, and prevent fraud, abuse, or security incidents.</li>
              <li>Communicate about updates, features, and service notices (you can manage preferences where offered).</li>
              <li>Analyze usage to improve reliability, performance, and user experience.</li>
              <li>Comply with legal obligations and enforce agreements.</li>
            </Typography>
          </Box>

          <Divider />

          {/* Sharing */}
          <Box sx={{ my: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: "bold", mb: 2 }}>
              How We Share Information
            </Typography>
            <Typography variant="body1" paragraph>
              We do not sell your personal information. We may share information with:
            </Typography>
            <Typography variant="body1" component="ul" sx={{ pl: 2 }}>
              <li><strong>Service Providers & Sub-processors:</strong> cloud hosting, analytics, communications, identity verification, and payment operations—bound by confidentiality and data protection terms.</li>
              <li><strong>Payments & Financial Partners:</strong> banks, payment networks, processors, program managers, funders/credit providers, and insurers to enable BlitzPay, settlement, and risk controls.</li>
              <li><strong>Enterprise Clients & Counterparties:</strong> to validate invoices, approvals, NOA delivery, and servicing/collections where applicable.</li>
              <li><strong>Corporate Transactions:</strong> mergers, financing, acquisition, or asset transfers.</li>
              <li><strong>Legal & Safety:</strong> to comply with law, respond to lawful requests, or protect rights, safety, and security.</li>
            </Typography>
          </Box>

          <Divider />

          {/* Google Workspace APIs */}
          <Box sx={{ my: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: "bold", mb: 2 }}>
              Use of Google Workspace APIs (Gmail)
            </Typography>
            <Typography variant="body1" paragraph>
              With your explicit consent, {COMPANY_SHORT} may connect to your Google account to
              help surface relevant deal emails (e.g., inbound brand offers) and streamline
              workflows. We request full Gmail API access to enable comprehensive email management
              for business collaboration:
            </Typography>
            <Typography variant="body1" component="ul" sx={{ pl: 2 }}>
              <li>
                <strong>Scope Requested:</strong>{" "}
                <code>https://mail.google.com/</code> — Full Gmail access for reading, composing,
                sending, and organizing emails related to brand partnerships and collaboration opportunities.
              </li>
            </Typography>
            <Typography variant="body1" paragraph sx={{ mt: 2 }}>
              This comprehensive scope enables:
            </Typography>
            <Typography variant="body1" component="ul" sx={{ pl: 2 }}>
              <li>
                <strong>Read Access:</strong> View email messages, threads, metadata, and labels to
                identify and classify collaboration opportunities and brand partnership inquiries.
              </li>
              <li>
                <strong>Compose & Send Access:</strong> Create and send emails on your behalf for
                automated responses, follow-ups, and brand deal communications through our CRM integration.
              </li>
              <li>
                <strong>Organize Access:</strong> Manage labels, mark messages as read/unread, and
                organize emails for workflow management without deleting content.
              </li>
              <li>
                <strong>Thread Management:</strong> Track conversations and maintain context throughout
                collaboration lifecycles.
              </li>
            </Typography>
          </Box>

          <Divider />

          {/* OAuth Token Security & Management */}
          <Box sx={{ my: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: "bold", mb: 2 }}>
              OAuth Token Security & Management
            </Typography>
            <Typography variant="body1" paragraph>
              When you authorize {COMPANY_SHORT} to connect with your Google account, we handle
              access credentials with the following protections:
            </Typography>
            <Typography variant="body1" component="ul" sx={{ pl: 2 }}>
              <li>
                <strong>Token Storage:</strong> We use a dual-layer security approach for token storage.
                User OAuth tokens (access and refresh tokens) are stored in our PostgreSQL database,
                which is encrypted at rest using Google Cloud Platform's Key Management Service (KMS).
                Service account credentials used for backend operations are stored in Fernet-encrypted
                JSON files (AES-128-CBC with HMAC-SHA256 authentication). No tokens are ever stored
                in plaintext or browser local storage.
              </li>
              <li>
                <strong>Token Lifecycle:</strong> Access tokens expire within 1 hour and are
                automatically refreshed using encrypted refresh tokens. We never request
                longer-lived credentials than necessary for service operation.
              </li>
              <li>
                <strong>No Third-Party Sharing:</strong> OAuth tokens are never shared with third
                parties, affiliates, or service providers. They remain within {COMPANY_SHORT}'s
                secure infrastructure solely to fulfill the authorized Google API requests you've
                approved.
              </li>
              <li>
                <strong>Revocation & Data Deletion:</strong> You can revoke {COMPANY_SHORT}'s access
                to your Google account at any time through your{" "}
                <MuiLink
                  href="https://myaccount.google.com/permissions"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Google Account settings
                </MuiLink>{" "}
                or by disconnecting within the {COMPANY_SHORT} app. Upon revocation, we immediately
                cease API access to your Gmail account and delete OAuth tokens from our database
                (typically immediate, guaranteed within 24 hours). Email data derived from Gmail is
                removed from active systems within 7 days, and from encrypted backup systems within
                30 days.
              </li>
              <li>
                <strong>Security Monitoring:</strong> We log all OAuth token usage and monitor for
                anomalies, unauthorized access attempts, or suspicious patterns. In the event of a
                detected security incident involving tokens, we follow our incident response
                procedures to contain, investigate, and remediate the issue promptly.
              </li>
            </Typography>
            <Typography variant="body1" paragraph>
              If you have questions about how we secure your Google account connection, please
              contact us at {CONTACT_EMAIL}.
            </Typography>
          </Box>

          <Typography variant="body1" paragraph>
            Our use of information received from Google APIs adheres to the{" "}
            <MuiLink
              href="https://developers.google.com/terms/api-services-user-data-policy"
              target="_blank"
              rel="noopener noreferrer"
            >
              Google API Services User Data Policy
            </MuiLink>
            , including Limited Use. We do <strong>not</strong> use Gmail data to serve ads or
            for generalized AI/ML model training. Human review occurs only with your
            affirmative agreement, to resolve abuse/security issues, to comply with law, or
            for internal operations where data is aggregated/limited.
          </Typography>

          <Divider />

          {/* Security */}
          <Box sx={{ my: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: "bold", mb: 2 }}>
              Security
            </Typography>
            <Typography variant="body1" paragraph>
              We implement technical and organizational measures designed to protect personal
              information (e.g., encryption in transit, access controls, logging). No method of
              transmission or storage is 100% secure, but we continuously improve safeguards
              and follow industry best practices. Where required, we execute DPAs and
              sub-processor agreements.
            </Typography>
          </Box>

          <Divider />

          {/* Data retention & deletion */}
          <Box sx={{ my: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: "bold", mb: 2 }}>
              Data Retention & Deletion
            </Typography>
            <Typography variant="body1" paragraph>
              We retain information for as long as necessary to provide the Services, meet
              legal/regulatory obligations (including financial record-keeping), resolve
              disputes, and enforce agreements. If you disconnect a Google account, we remove
              associated Google-sourced data from active systems within 30 days, subject to
              legally required retention. You can also request deletion (see "Your Rights").
            </Typography>
          </Box>

          <Divider />

          {/* Your rights */}
          <Box sx={{ my: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: "bold", mb: 2 }}>
              Your Rights
            </Typography>
            <Typography variant="body1" paragraph>
              Depending on your location, you may have rights to access, correct, delete, or
              restrict processing of your personal information, as well as data portability and
              the right to object. California (CPRA) and EU/UK (GDPR) residents have additional
              rights, including limiting certain uses and filing complaints with regulators.
              We will not discriminate against you for exercising your rights.
            </Typography>
            <Typography variant="body1" paragraph>
              To exercise rights or submit a privacy request, contact us at{" "}
              <Typography component="span" sx={{ fontStyle: "italic" }}>
                {CONTACT_EMAIL}
              </Typography>
              .
            </Typography>
          </Box>

          <Divider />

          {/* Cookies */}
          <Box sx={{ my: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: "bold", mb: 2 }}>
              Cookies & Similar Technologies
            </Typography>
            <Typography variant="body1" paragraph>
              We use cookies/local storage to remember preferences, keep you signed in, and
              analyze usage. You can control cookies through your browser settings; some
              features may not work as intended without them.
            </Typography>
          </Box>

          <Divider />

          {/* No AI/ML training */}
          <Box sx={{ my: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: "bold", mb: 2 }}>
              No Use of Google Workspace Data for AI/ML Training
            </Typography>
            <Typography variant="body1" paragraph>
              We do <strong>not</strong> use any data obtained through Google Workspace APIs to
              develop, improve, or train generalized AI or machine-learning models. Such data
              is used only for the limited purposes described in this Policy and in compliance
              with Google's policies.
            </Typography>
          </Box>

          <Divider />

          {/* Children */}
          <Box sx={{ my: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>
              Children's Privacy
            </Typography>
            <Typography variant="body1" paragraph>
              Our Services are not directed to children under 13 (or the age of digital
              consent where you live). We do not knowingly collect data from children in this
              age group. If you believe a child has provided personal data, please contact us.
            </Typography>
          </Box>

          <Divider />

          {/* Changes */}
          <Box sx={{ my: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: "bold", mb: 2 }}>
              Changes to This Policy
            </Typography>
            <Typography variant="body1" paragraph>
              We may update this Privacy Policy from time to time. We'll post the revised
              Policy with a new "Last updated" date. Material changes may also be communicated
              via the Services or email where appropriate.
            </Typography>
          </Box>

          <Divider />

          {/* Contact */}
          <Box sx={{ my: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: "bold", mb: 2 }}>
              Contact Us
            </Typography>
            <Typography variant="body1" paragraph>
              Questions about this Policy or our data practices?
            </Typography>
            <Typography variant="body1" paragraph>
              Email: {CONTACT_EMAIL}
              <br />
              Address: {CONTACT_ADDRESS}
            </Typography>
          </Box>
        </Box>
      </Container>

      <HomeFooter />
    </Box>
  );
};

export default PrivacyPolicy;